import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  LabelList,
  ReferenceArea,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BarChart3, ExternalLink, TriangleAlert } from "lucide-react";
import { fetchApi } from "@/api/client";
import type { Participant, SensorRecord } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner, ErrorDisplay } from "@/components/loading";
import { formatDateShort, relativeTime } from "@/lib/format-date";
import {
  formatGapDuration,
  formatSourceName,
  getUploadAlertBadgeLabel,
  getUploadAlertDescription,
  hasUploadAlert,
  PERIODIC_GAP_SOURCES,
} from "@/lib/upload-alerts";

const MST_TZ = "America/Phoenix";
const PAGE_LIMIT = 1000;
const DEFAULT_MAX_PERIODIC_RECORDS = 5000;
const DEFAULT_MAX_CONTEXT_RECORDS = 3000;
const FULL_MAX_PERIODIC_RECORDS = 25000;
const FULL_MAX_CONTEXT_RECORDS = 15000;
const BUCKET_MINUTES = 15;
const BUCKET_STEP_MS = BUCKET_MINUTES * 60_000;
// A single collectDue() sweep runs ALIVE -> AT_LOCATION -> PHYSICAL_ACTIVITY ->
// WIFI_EVENT (~5s scan) -> BLUETOOTH_SCAN (~5-12s scan) sequentially over ~12-20s.
// Always start a new sweep on ALIVE, and group subsequent non-ALIVE periodic
// sensors within 20s of the previous sensor (35s max total sweep duration) so
// boundary sweeps stay in one 15-minute slot without merging standalone events.
const SWEEP_MAX_STEP_MS = 20_000;
const SWEEP_MAX_TOTAL_MS = 35_000;

type TimeRangePreset = "24h" | "3d" | "7d" | "30d" | "custom";

const TIME_RANGE_OPTIONS: Array<{
  value: Exclude<TimeRangePreset, "custom">;
  label: string;
  durationMs: number;
}> = [
  { value: "24h", label: "24h", durationMs: 24 * 3600_000 },
  { value: "3d", label: "3d", durationMs: 3 * 24 * 3600_000 },
  { value: "7d", label: "7d", durationMs: 7 * 24 * 3600_000 },
  { value: "30d", label: "30d", durationMs: 30 * 24 * 3600_000 },
];

const PERIODIC_SOURCE_COLORS: Record<string, string> = {
  ALIVE: "#2563EB",
  BLUETOOTH_SCAN: "#7C3AED",
  PHYSICAL_ACTIVITY: "#059669",
  WIFI_EVENT: "#D97706",
};

function formatMstWithSeconds(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return (
    new Intl.DateTimeFormat("en-US", {
      timeZone: MST_TZ,
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d) + " MST"
  );
}

function formatUtcShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
}

function formatBucketLabelMst(ms: number): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: MST_TZ,
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));
}

function formatMstDayKey(ms: number): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: MST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(ms));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function formatMstDayLabel(dayKey: string): string {
  const ms = Date.parse(`${dayKey}T00:00:00-07:00`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: MST_TZ,
    month: "short",
    day: "numeric",
  }).format(new Date(ms));
}

function mstDayStartMs(dayKey: string): number {
  return Date.parse(`${dayKey}T00:00:00-07:00`);
}

function mstDayEndMs(dayKey: string): number {
  return Date.parse(`${dayKey}T23:59:59.999-07:00`);
}

interface PaginatedSensorsResult {
  records: SensorRecord[];
  truncated: boolean;
  maxRecords: number;
}

async function fetchPaginatedSensors(params: {
  user_id: string;
  source?: string;
  from: string;
  to: string;
  maxRecords: number;
}): Promise<PaginatedSensorsResult> {
  const out: SensorRecord[] = [];
  let cursor: string | undefined;
  do {
    const queryParams: Record<string, string | undefined> = {
      user_id: params.user_id,
      from: params.from,
      to: params.to,
      limit: String(PAGE_LIMIT),
      cursor,
    };
    if (params.source) {
      queryParams.source = params.source;
    }
    const res = await fetchApi<SensorRecord[]>("/sensors", queryParams);
    out.push(...res.data);
    cursor = res.next_cursor ?? undefined;
  } while (cursor && out.length < params.maxRecords);
  return {
    records: out,
    truncated: Boolean(cursor),
    maxRecords: params.maxRecords,
  };
}

interface PeriodicSweep {
  startMs: number;
  endMs: number;
  startRecord: SensorRecord;
  endRecord: SensorRecord;
  records: SensorRecord[];
}

interface SweepInterval {
  startMs: number;
  endMs: number;
  gapSeconds: number;
  gapMinutes: number;
  startIso: string;
  endIso: string;
  startSource: string;
  endSource: string;
}

interface TimelineBucketDatum {
  bucketMs: number;
  label: string;
  mstRange: string;
  utcRange: string;
  ALIVE: number;
  BLUETOOTH_SCAN: number;
  PHYSICAL_ACTIVITY: number;
  WIFI_EVENT: number;
  otherCount: number;
  periodicTotal: number;
  inReportedGap: boolean;
}

interface DailyCollectionDatum {
  day: string;
  label: string;
  periodic: number;
  survey: number;
  other: number;
  total: number;
  surveyTimes: string[];
}

function formatMstTimeShort(ms: number): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: MST_TZ,
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(ms));
}

interface SourceGapSummary {
  source: string;
  label: string;
  color: string;
  count: number;
  lastEventIso: string | null;
  maxGapSeconds: number;
}

export interface UploadAlertDialogProps {
  participant: Participant | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showParticipantLink?: boolean;
}

export function UploadAlertDialog({
  participant,
  open,
  onOpenChange,
  showParticipantLink = true,
}: UploadAlertDialogProps) {
  const [includeOtherSources, setIncludeOtherSources] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRangePreset>("24h");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [expandedLimitsForUser, setExpandedLimitsForUser] = useState<
    string | null
  >(null);
  const useExpandedLimits =
    Boolean(participant?.user_id) &&
    expandedLimitsForUser === participant?.user_id;
  const maxPeriodicRecords = useExpandedLimits
    ? FULL_MAX_PERIODIC_RECORDS
    : DEFAULT_MAX_PERIODIC_RECORDS;
  const maxContextRecords = useExpandedLimits
    ? FULL_MAX_CONTEXT_RECORDS
    : DEFAULT_MAX_CONTEXT_RECORDS;

  const periodicSources = useMemo(
    () =>
      participant?.capture_gap_sources?.length
        ? participant.capture_gap_sources.map((s) => s.toUpperCase())
        : [...PERIODIC_GAP_SOURCES],
    [participant],
  );

  const windowBounds = useMemo(() => {
    if (!participant) return null;
    const gapStartIso =
      participant.capture_gap_started_at || participant.upload_gap_started_at || "";
    const gapEndIso =
      participant.capture_gap_ended_at || participant.upload_gap_ended_at || "";
    const lastUpIso = participant.last_uploaded_at || participant.last_seen || "";

    const gapStartMs = gapStartIso ? new Date(gapStartIso).getTime() : NaN;
    const gapEndMs = gapEndIso ? new Date(gapEndIso).getTime() : NaN;
    const lastUpMs = lastUpIso ? new Date(lastUpIso).getTime() : NaN;
    const firstSeenMs = participant.first_seen
      ? new Date(participant.first_seen).getTime()
      : NaN;

    const referenceEndMs =
      Math.max(
        Number.isFinite(gapEndMs) ? gapEndMs : 0,
        Number.isFinite(lastUpMs) ? lastUpMs : 0,
        Number.isFinite(firstSeenMs) ? firstSeenMs : 0,
      );
    if (!referenceEndMs) return null;

    let displayStartMs: number;
    let displayEndMs: number;
    if (timeRange === "custom") {
      const customStartMs = customFromDate
        ? mstDayStartMs(customFromDate)
        : referenceEndMs - 7 * 24 * 3600_000;
      const customEndMs = customToDate
        ? mstDayEndMs(customToDate)
        : referenceEndMs;
      displayStartMs = Math.min(customStartMs, customEndMs);
      displayEndMs = Math.max(customStartMs, customEndMs);
    } else {
      const option = TIME_RANGE_OPTIONS.find((item) => item.value === timeRange);
      displayEndMs = referenceEndMs;
      displayStartMs = referenceEndMs - (option?.durationMs ?? 24 * 3600_000);
    }

    const fetchPaddingMs = 30 * 60_000;

    return {
      fromIso: new Date(displayStartMs - fetchPaddingMs).toISOString(),
      toIso: new Date(displayEndMs + fetchPaddingMs).toISOString(),
      fromMs: displayStartMs,
      toMs: displayEndMs,
      gapStartMs: Number.isFinite(gapStartMs) ? gapStartMs : null,
      gapEndMs: Number.isFinite(gapEndMs) ? gapEndMs : null,
      gapStartIso: gapStartIso || null,
      gapEndIso: gapEndIso || null,
    };
  }, [participant, timeRange, customFromDate, customToDate]);

  const query = useQuery({
    queryKey: [
      "upload-alert-inspect",
      participant?.user_id,
      windowBounds?.fromIso,
      windowBounds?.toIso,
      periodicSources.join(","),
      maxPeriodicRecords,
      maxContextRecords,
    ],
    queryFn: async () => {
      if (!participant || !windowBounds) {
        return {
          periodicRecords: [] as SensorRecord[],
          allRecords: [] as SensorRecord[],
          surveyRecords: [] as SensorRecord[],
          periodicTruncated: false,
          contextTruncated: false,
          maxPeriodicRecords,
          maxContextRecords,
        };
      }
      const [periodicResult, contextResult, surveyResult] = await Promise.all([
        fetchPaginatedSensors({
          user_id: participant.user_id,
          source: periodicSources.join(","),
          from: windowBounds.fromIso,
          to: windowBounds.toIso,
          maxRecords: maxPeriodicRecords,
        }),
        fetchPaginatedSensors({
          user_id: participant.user_id,
          from: windowBounds.fromIso,
          to: windowBounds.toIso,
          maxRecords: maxContextRecords,
        }),
        fetchPaginatedSensors({
          user_id: participant.user_id,
          source: "survey",
          from: windowBounds.fromIso,
          to: windowBounds.toIso,
          maxRecords: 1000,
        }),
      ]);
      return {
        periodicRecords: periodicResult.records,
        allRecords: contextResult.records,
        surveyRecords: surveyResult.records,
        periodicTruncated: periodicResult.truncated,
        contextTruncated: contextResult.truncated,
        maxPeriodicRecords: periodicResult.maxRecords,
        maxContextRecords: contextResult.maxRecords,
      };
    },
    enabled: open && Boolean(participant && windowBounds),
    staleTime: 30_000,
  });

  const analysis = useMemo(() => {
    if (!participant || !windowBounds || !query.data) return null;
    const periodicSet = new Set(periodicSources);
    const periodicTruncated = Boolean(query.data.periodicTruncated);
    const contextTruncated = Boolean(query.data.contextTruncated);
    const isTruncated = periodicTruncated || contextTruncated;

    // Deduplicate and merge records
    const byKey = new Map<string, SensorRecord>();
    for (const r of [
      ...query.data.periodicRecords,
      ...query.data.allRecords,
      ...(query.data.surveyRecords ?? []),
    ]) {
      if (r.source === "survey_state") continue;
      const key = `${r.collected_at}|${r.source}|${r.uuid ?? ""}`;
      byKey.set(key, r);
    }

    const allSorted = [...byKey.values()]
      .map((r) => ({ record: r, ms: new Date(r.collected_at).getTime() }))
      .filter((x) => Number.isFinite(x.ms))
      .sort((a, b) => a.ms - b.ms);

    const periodicSorted = allSorted.filter((x) =>
      periodicSet.has(x.record.source.toUpperCase()),
    );

    const gapStartMs = windowBounds.gapStartMs;
    const gapEndMs = windowBounds.gapEndMs;

    // Events recorded inside the reported gap window
    const eventsInGap =
      gapStartMs !== null && gapEndMs !== null
        ? allSorted.filter((x) => x.ms > gapStartMs + 1000 && x.ms < gapEndMs - 1000)
        : [];
    const nonPeriodicInGapBySource = new Map<string, number>();
    for (const x of eventsInGap) {
      if (!periodicSet.has(x.record.source.toUpperCase())) {
        nonPeriodicInGapBySource.set(
          x.record.source,
          (nonPeriodicInGapBySource.get(x.record.source) ?? 0) + 1,
        );
      }
    }

    // Group sequential periodic events of a single collection sweep (~15s:
    // ALIVE -> PHYSICAL_ACTIVITY -> WIFI_EVENT -> BLUETOOTH_SCAN) into the
    // sweep's start timestamp so boundary runs never split across adjacent
    // 15-minute slots, while always starting a new sweep on ALIVE.
    const sweeps: PeriodicSweep[] = [];
    for (const item of periodicSorted) {
      const srcUpper = item.record.source.toUpperCase();
      const currentSweep = sweeps[sweeps.length - 1];
      if (
        currentSweep &&
        srcUpper !== "ALIVE" &&
        item.ms - currentSweep.endMs <= SWEEP_MAX_STEP_MS &&
        item.ms - currentSweep.startMs <= SWEEP_MAX_TOTAL_MS
      ) {
        currentSweep.endMs = item.ms;
        currentSweep.endRecord = item.record;
        currentSweep.records.push(item.record);
      } else {
        sweeps.push({
          startMs: item.ms,
          endMs: item.ms,
          startRecord: item.record,
          endRecord: item.record,
          records: [item.record],
        });
      }
    }

    const firstPeriodicMs =
      periodicSorted.length > 0 ? periodicSorted[0].ms : null;
    const effectiveEndRaw = windowBounds.toMs;
    const effectiveStartRaw =
      periodicTruncated && firstPeriodicMs !== null
        ? Math.max(windowBounds.fromMs, firstPeriodicMs)
        : windowBounds.fromMs;

    const sweepsInWindow = sweeps.filter(
      (sweep) => sweep.endMs >= effectiveStartRaw && sweep.startMs <= effectiveEndRaw,
    );
    const firstSeenMs = participant.first_seen
      ? new Date(participant.first_seen).getTime()
      : NaN;
    const continuityStartMs = Number.isFinite(firstSeenMs)
      ? Math.max(effectiveStartRaw, firstSeenMs)
      : effectiveStartRaw;
    const sweepIntervals: SweepInterval[] = [];

    if (sweepsInWindow.length === 0) {
      if (continuityStartMs < effectiveEndRaw) {
        const diffSec = Math.round(
          (effectiveEndRaw - continuityStartMs) / 1000,
        );
        sweepIntervals.push({
          startMs: continuityStartMs,
          endMs: effectiveEndRaw,
          gapSeconds: diffSec,
          gapMinutes: Math.round((diffSec / 60) * 10) / 10,
          startIso: new Date(continuityStartMs).toISOString(),
          endIso: new Date(effectiveEndRaw).toISOString(),
          startSource: "Window Start",
          endSource: "Window End",
        });
      }
    } else {
      const firstSweep = sweepsInWindow[0];
      if (firstSweep.startMs > continuityStartMs) {
        const diffSec = Math.round(
          (firstSweep.startMs - continuityStartMs) / 1000,
        );
        sweepIntervals.push({
          startMs: continuityStartMs,
          endMs: firstSweep.startMs,
          gapSeconds: diffSec,
          gapMinutes: Math.round((diffSec / 60) * 10) / 10,
          startIso: new Date(continuityStartMs).toISOString(),
          endIso: firstSweep.startRecord.collected_at,
          startSource: "Window Start",
          endSource: formatSourceName(firstSweep.startRecord.source),
        });
      }

      for (let i = 0; i < sweepsInWindow.length - 1; i++) {
        const prev = sweepsInWindow[i];
        const next = sweepsInWindow[i + 1];
        const diffSec = Math.max(
          0,
          Math.round((next.startMs - prev.endMs) / 1000),
        );
        sweepIntervals.push({
          startMs: prev.endMs,
          endMs: next.startMs,
          gapSeconds: diffSec,
          gapMinutes: Math.round((diffSec / 60) * 10) / 10,
          startIso: prev.endRecord.collected_at,
          endIso: next.startRecord.collected_at,
          startSource: formatSourceName(prev.endRecord.source),
          endSource: formatSourceName(next.startRecord.source),
        });
      }

      const lastSweep = sweepsInWindow[sweepsInWindow.length - 1];
      if (lastSweep.endMs < effectiveEndRaw) {
        const diffSec = Math.round((effectiveEndRaw - lastSweep.endMs) / 1000);
        sweepIntervals.push({
          startMs: lastSweep.endMs,
          endMs: effectiveEndRaw,
          gapSeconds: diffSec,
          gapMinutes: Math.round((diffSec / 60) * 10) / 10,
          startIso: lastSweep.endRecord.collected_at,
          endIso: new Date(effectiveEndRaw).toISOString(),
          startSource: formatSourceName(lastSweep.endRecord.source),
          endSource: "Window End",
        });
      }
    }

    const largestGap = sweepIntervals.reduce<SweepInterval | null>(
      (largest, interval) =>
        !largest || interval.gapSeconds > largest.gapSeconds ? interval : largest,
      null,
    );

    const firstBucketMs =
      Math.floor(effectiveStartRaw / BUCKET_STEP_MS) * BUCKET_STEP_MS;
    const lastBucketMs =
      Math.floor(effectiveEndRaw / BUCKET_STEP_MS) * BUCKET_STEP_MS;
    const bucketMap = new Map<number, TimelineBucketDatum>();
    for (let t = firstBucketMs; t <= lastBucketMs; t += BUCKET_STEP_MS) {
      const bucketEnd = t + BUCKET_STEP_MS;
      bucketMap.set(t, {
        bucketMs: t,
        label: formatBucketLabelMst(t),
        mstRange: `${formatDateShort(new Date(t).toISOString())} – ${formatDateShort(new Date(bucketEnd).toISOString())} MST`,
        utcRange: `${formatUtcShort(new Date(t).toISOString())} – ${formatUtcShort(new Date(bucketEnd).toISOString())}`,
        ALIVE: 0,
        BLUETOOTH_SCAN: 0,
        PHYSICAL_ACTIVITY: 0,
        WIFI_EVENT: 0,
        otherCount: 0,
        periodicTotal: 0,
        inReportedGap: false,
      });
    }

    // Place all periodic records of each sweep into the bucket of `sweep.startMs`
    // so a sweep that crosses a slot boundary stays together in one 15-minute slot.
    for (const sweep of sweeps) {
      const bMs = Math.floor(sweep.startMs / BUCKET_STEP_MS) * BUCKET_STEP_MS;
      const bucket = bucketMap.get(bMs);
      if (!bucket) continue;
      for (const record of sweep.records) {
        const srcUpper = record.source.toUpperCase();
        if (srcUpper === "ALIVE") {
          bucket.ALIVE += 1;
          bucket.periodicTotal += 1;
        } else if (srcUpper === "BLUETOOTH_SCAN") {
          bucket.BLUETOOTH_SCAN += 1;
          bucket.periodicTotal += 1;
        } else if (srcUpper === "PHYSICAL_ACTIVITY") {
          bucket.PHYSICAL_ACTIVITY += 1;
          bucket.periodicTotal += 1;
        } else if (srcUpper === "WIFI_EVENT") {
          bucket.WIFI_EVENT += 1;
          bucket.periodicTotal += 1;
        }
      }
    }

    // Place non-periodic records into their respective buckets
    for (const { record, ms } of allSorted) {
      if (periodicSet.has(record.source.toUpperCase())) continue;
      const bMs = Math.floor(ms / BUCKET_STEP_MS) * BUCKET_STEP_MS;
      const bucket = bucketMap.get(bMs);
      if (bucket) {
        bucket.otherCount += 1;
      }
    }

    // Mark `inReportedGap` only on empty (0 periodic event) slots inside the reported
    // gap window so the red shaded ReferenceArea does not overlap the valid pre-gap or
    // post-gap capture bars at the boundaries of the gap.
    if (gapStartMs !== null && gapEndMs !== null) {
      const overlapping = [...bucketMap.values()].filter(
        (b) => b.bucketMs + BUCKET_STEP_MS > gapStartMs && b.bucketMs < gapEndMs,
      );
      const emptyOverlapping = overlapping.filter((b) => b.periodicTotal === 0);
      const targetGapBuckets =
        emptyOverlapping.length > 0
          ? emptyOverlapping
          : overlapping.filter(
              (b) =>
                b.bucketMs >= gapStartMs &&
                b.bucketMs + BUCKET_STEP_MS <= gapEndMs,
            );
      for (const b of targetGapBuckets) {
        b.inReportedGap = true;
      }
    }

    const timeline = [...bucketMap.values()];
    const gapBuckets = timeline.filter((b) => b.inReportedGap);
    const gapHighlightStartLabel = gapBuckets[0]?.label;
    const gapHighlightEndLabel = gapBuckets[gapBuckets.length - 1]?.label;

    const dailyMap = new Map<string, DailyCollectionDatum>();
    const firstDayMs = mstDayStartMs(formatMstDayKey(windowBounds.fromMs));
    const lastDayMs = mstDayStartMs(formatMstDayKey(windowBounds.toMs));
    for (let dayMs = firstDayMs; dayMs <= lastDayMs; dayMs += 24 * 3600_000) {
      const day = formatMstDayKey(dayMs);
      dailyMap.set(day, {
        day,
        label: formatMstDayLabel(day),
        periodic: 0,
        survey: 0,
        other: 0,
        total: 0,
        surveyTimes: [],
      });
    }
    const windowAllSorted = allSorted.filter(
      (item) => item.ms >= windowBounds.fromMs && item.ms <= windowBounds.toMs,
    );
    for (const { record, ms } of windowAllSorted) {
      const daily = dailyMap.get(formatMstDayKey(ms));
      if (!daily) continue;
      const source = record.source.toUpperCase();
      if (periodicSet.has(source)) {
        daily.periodic += 1;
      } else if (source === "SURVEY") {
        daily.survey += 1;
        const slotId =
          typeof record.values?.slot_id === "string" && record.values.slot_id.trim()
            ? record.values.slot_id.trim()
            : null;
        daily.surveyTimes.push(
          slotId ? `${formatMstTimeShort(ms)} (${slotId})` : formatMstTimeShort(ms),
        );
      } else {
        daily.other += 1;
      }
      daily.total += 1;
    }
    const dailyCollection = [...dailyMap.values()];
    const surveyCount = dailyCollection.reduce((sum, day) => sum + day.survey, 0);

    const windowPeriodicSorted = periodicSorted.filter(
      (x) => x.ms >= windowBounds.fromMs && x.ms <= windowBounds.toMs,
    );
    const perSource: SourceGapSummary[] = periodicSources.map((src) => {
      const windowEvents = windowPeriodicSorted.filter(
        (x) => x.record.source.toUpperCase() === src,
      );
      const checkpointTimes: number[] = [
        Math.min(effectiveEndRaw, continuityStartMs),
      ];
      for (const ev of windowEvents) {
        if (ev.ms > checkpointTimes[checkpointTimes.length - 1]) {
          checkpointTimes.push(ev.ms);
        }
      }
      if (
        effectiveEndRaw > checkpointTimes[checkpointTimes.length - 1]
      ) {
        checkpointTimes.push(effectiveEndRaw);
      }

      let maxGapSec = 0;
      for (let i = 0; i < checkpointTimes.length - 1; i++) {
        const diff = Math.round(
          (checkpointTimes[i + 1] - checkpointTimes[i]) / 1000,
        );
        if (diff > maxGapSec) maxGapSec = diff;
      }

      return {
        source: src,
        label: formatSourceName(src),
        color: PERIODIC_SOURCE_COLORS[src] ?? "#2563EB",
        count: windowEvents.length,
        lastEventIso: windowEvents[windowEvents.length - 1]?.record.collected_at ?? null,
        maxGapSeconds: maxGapSec,
      };
    });

    const lastPeriodic = windowPeriodicSorted[windowPeriodicSorted.length - 1];
    const latestRecordOverall =
      windowAllSorted[windowAllSorted.length - 1]?.record ?? null;

    return {
      periodicCount: windowPeriodicSorted.length,
      fetchedPeriodicCount: query.data.periodicRecords.length,
      fetchedContextCount: query.data.allRecords.length,
      periodicTruncated,
      contextTruncated,
      isTruncated,
      maxPeriodicRecords: query.data.maxPeriodicRecords,
      maxContextRecords: query.data.maxContextRecords,
      oldestPeriodicIso: periodicSorted[0]?.record.collected_at ?? null,
      oldestOverallIso: allSorted[0]?.record.collected_at ?? null,
      lastPeriodicRecord: lastPeriodic?.record ?? null,
      latestRecordOverall,
      largestGap,
      dailyCollection,
      surveyCount,
      nonPeriodicInGap: [...nonPeriodicInGapBySource.entries()],
      timeline,
      bucketStepMinutes: BUCKET_MINUTES,
      gapHighlightStartLabel,
      gapHighlightEndLabel,
      perSource,
    };
  }, [participant, windowBounds, query.data, periodicSources]);

  if (!participant) return null;

  const displayName = participant.username || participant.user_id.slice(0, 8);
  const gapStartIso =
    participant.capture_gap_started_at || participant.upload_gap_started_at;
  const gapEndIso =
    participant.capture_gap_ended_at || participant.upload_gap_ended_at;
  const gapDurationSec =
    participant.capture_gap_duration_seconds ||
    participant.upload_gap_duration_seconds ||
    0;
  const hasActiveAlert = hasUploadAlert(participant);
  const hasReportedGap =
    participant.upload_alert === "gap_1h" && Boolean(gapStartIso && gapEndIso);

  function selectTimeRange(nextRange: TimeRangePreset) {
    if (nextRange === "custom" && (!customFromDate || !customToDate)) {
      const referenceIso =
        participant?.last_uploaded_at ||
        participant?.last_seen ||
        participant?.first_seen;
      if (!referenceIso) {
        setTimeRange(nextRange);
        return;
      }
      const referenceMs = new Date(referenceIso).getTime();
      setCustomFromDate(formatMstDayKey(referenceMs - 7 * 24 * 3600_000));
      setCustomToDate(formatMstDayKey(referenceMs));
    }
    setTimeRange(nextRange);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-3 pr-6">
            <div className="flex items-center gap-2.5">
              {hasActiveAlert ? (
                <TriangleAlert className="size-5 text-amber-600" />
              ) : (
                <BarChart3 className="size-5 text-blue-600" />
              )}
              <DialogTitle>
                Collection Gap Analysis —{" "}
                <span className="font-mono">{displayName}</span>
              </DialogTitle>
              {hasActiveAlert ? (
                <Badge className="bg-red-100 text-red-800 border-red-200">
                  {getUploadAlertBadgeLabel(participant)}
                </Badge>
              ) : (
                <Badge className="border-blue-200 bg-blue-50 text-blue-800">
                  No active alert
                </Badge>
              )}
              {analysis?.isTruncated && (
                <Badge className="bg-amber-100 text-amber-900 border-amber-300">
                  Records Truncated
                </Badge>
              )}
            </div>
            {showParticipantLink && (
              <Button
                variant="outline"
                size="sm"
                render={<Link to={`/participants/${participant.user_id}`} />}
                onClick={() => onOpenChange(false)}
              >
                Open Participant
                <ExternalLink className="ml-1.5 size-3.5" />
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {hasActiveAlert
              ? getUploadAlertDescription(participant)
              : "Inspect periodic collection continuity and daily data volume for this participant."}
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-3 rounded-lg border bg-slate-50/70 p-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-xs font-medium text-slate-700">Time range</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {TIME_RANGE_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={timeRange === option.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => selectTimeRange(option.value)}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                variant={timeRange === "custom" ? "default" : "outline"}
                size="sm"
                onClick={() => selectTimeRange("custom")}
              >
                Custom
              </Button>
            </div>
          </div>
          {timeRange === "custom" && (
            <div className="flex flex-wrap gap-2">
              <label className="text-xs text-muted-foreground">
                From
                <Input
                  type="date"
                  value={customFromDate}
                  onChange={(event) => setCustomFromDate(event.target.value)}
                  className="mt-1 h-8 w-36 bg-white"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                To
                <Input
                  type="date"
                  value={customToDate}
                  onChange={(event) => setCustomToDate(event.target.value)}
                  className="mt-1 h-8 w-36 bg-white"
                />
              </label>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div
            className={
              hasReportedGap
                ? "rounded-lg border border-red-200 bg-red-50/60 p-3"
                : "rounded-lg border border-amber-200 bg-amber-50/60 p-3"
            }
          >
            <div
              className={`text-xs font-medium ${hasReportedGap ? "text-red-800" : "text-amber-800"}`}
            >
              {hasReportedGap ? "Reported Gap Window" : "Largest Periodic Gap"}
            </div>
            <div
              className={`mt-1 text-base font-bold ${hasReportedGap ? "text-red-950" : "text-amber-950"}`}
            >
              {formatGapDuration(
                hasReportedGap
                  ? gapDurationSec
                  : analysis?.largestGap?.gapSeconds ?? 0,
              )}
            </div>
            {hasReportedGap && gapStartIso && gapEndIso ? (
              <>
                <div className="mt-1 text-xs font-medium text-red-900">
                  {formatDateShort(gapStartIso)} → {formatDateShort(gapEndIso)} MST
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-red-800/80">
                  {formatUtcShort(gapStartIso)} → {formatUtcShort(gapEndIso)}
                </div>
              </>
            ) : analysis?.largestGap ? (
              <>
                <div className="mt-1 text-xs font-medium text-amber-900">
                  {formatDateShort(analysis.largestGap.startIso)} →{" "}
                  {formatDateShort(analysis.largestGap.endIso)} MST
                </div>
                <div className="mt-0.5 text-[11px] text-amber-800/80">
                  {analysis.largestGap.startSource} → {analysis.largestGap.endSource}
                </div>
              </>
            ) : (
              <div className="mt-1 text-xs text-amber-900">
                {query.isLoading ? "Checking..." : "No interval available"}
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-slate-50/70 p-3">
            <div className="text-xs font-medium text-muted-foreground">
              Latest Periodic Capture
            </div>
            {analysis?.lastPeriodicRecord ? (
              <>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {relativeTime(analysis.lastPeriodicRecord.collected_at)}
                </div>
                <div className="mt-1 text-xs text-slate-700">
                  {formatDateShort(analysis.lastPeriodicRecord.collected_at)} MST
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  Source:{" "}
                  {formatSourceName(analysis.lastPeriodicRecord.source)}
                </div>
              </>
            ) : (
              <div className="mt-1 text-xs text-slate-600">
                {query.isLoading
                  ? "Checking..."
                  : "None in inspection window"}
              </div>
            )}
          </div>

          <div className="rounded-lg border bg-slate-50/70 p-3">
            <div className="text-xs font-medium text-muted-foreground">
              Last Server Upload (Any Source)
            </div>
            {participant.last_uploaded_at ? (
              <>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {relativeTime(participant.last_uploaded_at)}
                </div>
                <div className="mt-1 text-xs text-slate-700">
                  {formatDateShort(participant.last_uploaded_at)} MST
                </div>
                {analysis?.latestRecordOverall && (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    Latest event:{" "}
                    {formatSourceName(analysis.latestRecordOverall.source)}
                  </div>
                )}
              </>
            ) : (
              <div className="mt-1 text-xs text-slate-600">No uploads recorded</div>
            )}
          </div>

          <div className="rounded-lg border bg-slate-50/70 p-3">
            <div className="text-xs font-medium text-muted-foreground">
              Surveys in Selected Range
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-900">
              {analysis ? analysis.surveyCount.toLocaleString() : "Loading..."}
            </div>
            {hasReportedGap && analysis ? (
              analysis.nonPeriodicInGap.length > 0 ? (
                <div className="mt-1 text-xs text-amber-800">
                  Other events inside the reported gap:{" "}
                  {analysis.nonPeriodicInGap
                    .map(([src, cnt]) => `${cnt} ${formatSourceName(src)}`)
                    .join(", ")}
                </div>
              ) : analysis.contextTruncated ? (
                <div className="mt-1 text-xs text-amber-800">
                  Context query reached{" "}
                  {analysis.fetchedContextCount.toLocaleString()} records;
                  earlier non-periodic activity may be truncated.
                </div>
              ) : (
                <div className="mt-1 text-xs text-muted-foreground">
                  No non-periodic events inside the reported gap.
                </div>
              )
            ) : (
              <div className="mt-1 text-xs text-muted-foreground">
                Included in the daily collection chart below.
              </div>
            )}
          </div>
        </div>

        {query.isLoading && <Spinner />}
        {query.isError && (
          <ErrorDisplay message="Failed to load sensor events for gap analysis" />
        )}

        {!query.isLoading && !query.isError && analysis && (
          <div className="space-y-4">
            {analysis.isTruncated && (
              <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50/90 p-3 text-xs text-amber-950 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 font-semibold text-amber-900">
                    <TriangleAlert className="size-4 shrink-0 text-amber-700" />
                    <span>
                      Sensor history in this inspection window was truncated at
                      the fetch limit
                    </span>
                  </div>
                  <div className="text-amber-900/90">
                    Loaded{" "}
                    <span className="font-mono font-medium">
                      {analysis.fetchedPeriodicCount.toLocaleString()}
                    </span>{" "}
                    periodic records
                    {analysis.periodicTruncated
                      ? ` (hit ${analysis.maxPeriodicRecords.toLocaleString()} cap)`
                      : ""}{" "}
                    and{" "}
                    <span className="font-mono font-medium">
                      {analysis.fetchedContextCount.toLocaleString()}
                    </span>{" "}
                    context records
                    {analysis.contextTruncated
                      ? ` (hit ${analysis.maxContextRecords.toLocaleString()} cap)`
                      : ""}
                    .
                    {analysis.oldestPeriodicIso && (
                      <>
                        {" "}
                        Earliest loaded periodic event:{" "}
                        <span className="font-mono font-medium">
                          {formatMstWithSeconds(analysis.oldestPeriodicIso)}
                        </span>
                        .
                      </>
                    )}{" "}
                    Earlier intervals in the window are omitted to prevent
                    fabricating gaps from unfetched pages.
                  </div>
                </div>
                {!useExpandedLimits && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-amber-400 bg-white text-amber-950 hover:bg-amber-100"
                    onClick={() => setExpandedLimitsForUser(participant.user_id)}
                  >
                    Load More Records (up to{" "}
                    {FULL_MAX_PERIODIC_RECORDS.toLocaleString()})
                  </Button>
                )}
              </div>
            )}
            {/* Chart 1: Continuous Zero-Filled Timeline with Sweep Coalescing & Shaded Gap */}
            <div className="rounded-lg border p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-medium text-[#1E293B]">
                  Continuous Capture Timeline (15-minute intervals, MST)
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIncludeOtherSources((prev) => !prev)}
                  >
                    {includeOtherSources
                      ? "Periodic sources only"
                      : "Include non-periodic sources"}
                  </Button>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={analysis.timeline}
                  margin={{ top: 12, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    minTickGap={28}
                  />
                  <YAxis
                    width={48}
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    label={{
                      value: "Events",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11, fill: "#64748B" },
                    }}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }
                      const row = payload[0]?.payload as
                        | TimelineBucketDatum
                        | undefined;
                      if (!row) return null;
                      return (
                        <div className="rounded-md border bg-white px-3 py-2 text-xs shadow-md">
                          <div className="font-semibold text-slate-900">
                            {row.mstRange}
                          </div>
                          <div className="mb-1.5 font-mono text-[10px] text-muted-foreground">
                            {row.utcRange}
                          </div>
                          {row.inReportedGap && (
                            <div className="mb-1.5 rounded bg-red-50 px-1.5 py-0.5 font-medium text-red-700">
                              Inside reported capture gap
                            </div>
                          )}
                          <div className="space-y-0.5">
                            <div>
                              <span className="font-medium text-blue-600">
                                Alive:
                              </span>{" "}
                              {row.ALIVE}
                            </div>
                            <div>
                              <span className="font-medium text-purple-600">
                                Bluetooth Scan:
                              </span>{" "}
                              {row.BLUETOOTH_SCAN}
                            </div>
                            <div>
                              <span className="font-medium text-emerald-600">
                                Physical Activity:
                              </span>{" "}
                              {row.PHYSICAL_ACTIVITY}
                            </div>
                            <div>
                              <span className="font-medium text-amber-600">
                                Wifi Event:
                              </span>{" "}
                              {row.WIFI_EVENT}
                            </div>
                            {includeOtherSources && (
                              <div>
                                <span className="font-medium text-slate-500">
                                  Other Sources:
                                </span>{" "}
                                {row.otherCount}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {analysis.gapHighlightStartLabel &&
                    analysis.gapHighlightEndLabel && (
                      <ReferenceArea
                        x1={analysis.gapHighlightStartLabel}
                        x2={analysis.gapHighlightEndLabel}
                        fill="#EF4444"
                        fillOpacity={0.16}
                        stroke="#DC2626"
                        strokeDasharray="4 4"
                      />
                    )}
                  <Bar
                    dataKey="ALIVE"
                    name="Alive"
                    stackId="a"
                    fill={PERIODIC_SOURCE_COLORS.ALIVE}
                  />
                  <Bar
                    dataKey="BLUETOOTH_SCAN"
                    name="Bluetooth Scan"
                    stackId="a"
                    fill={PERIODIC_SOURCE_COLORS.BLUETOOTH_SCAN}
                  />
                  <Bar
                    dataKey="PHYSICAL_ACTIVITY"
                    name="Physical Activity"
                    stackId="a"
                    fill={PERIODIC_SOURCE_COLORS.PHYSICAL_ACTIVITY}
                  />
                  <Bar
                    dataKey="WIFI_EVENT"
                    name="Wifi Event"
                    stackId="a"
                    fill={PERIODIC_SOURCE_COLORS.WIFI_EVENT}
                  />
                  {includeOtherSources && (
                    <Bar
                      dataKey="otherCount"
                      name="Other Sources (Screen, Sleep, Survey, etc.)"
                      stackId="a"
                      fill="#94A3B8"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium text-[#1E293B]">
                    Daily Data Collected & Survey Submissions
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Sensor volume on left axis · Survey count on right axis (MST)
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className={
                    analysis.surveyCount > 0
                      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "text-muted-foreground"
                  }
                >
                  {analysis.surveyCount} survey{analysis.surveyCount === 1 ? "" : "s"} in range ·{" "}
                  {analysis.dailyCollection.filter((d) => d.survey > 0).length}/
                  {analysis.dailyCollection.length} days
                </Badge>
              </div>

              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={analysis.dailyCollection}
                  margin={{ top: 18, right: 12, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    minTickGap={20}
                  />
                  <YAxis
                    yAxisId="left"
                    width={52}
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                    label={{
                      value: "Sensor Records",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 11, fill: "#64748B" },
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    width={48}
                    domain={[0, (dataMax: number) => Math.max(2, dataMax + 1)]}
                    tick={{ fontSize: 11, fill: "#047857" }}
                    allowDecimals={false}
                    label={{
                      value: "Surveys",
                      angle: 90,
                      position: "insideRight",
                      style: { fontSize: 11, fill: "#047857" },
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }
                      const row = payload[0]?.payload as
                        | DailyCollectionDatum
                        | undefined;
                      if (!row) return null;
                      return (
                        <div className="rounded-md border bg-white px-3 py-2 text-xs shadow-md">
                          <div className="font-semibold text-slate-900">
                            {row.label} MST
                          </div>
                          <div className="mt-1 space-y-0.5 text-slate-700">
                            <div>
                              <span className="font-medium text-blue-600">
                                Periodic data:
                              </span>{" "}
                              {row.periodic.toLocaleString()}
                            </div>
                            <div>
                              <span className="font-medium text-slate-600">
                                Other sensor data:
                              </span>{" "}
                              {row.other.toLocaleString()}
                            </div>
                            <div className="pt-0.5">
                              <span className="font-semibold text-emerald-700">
                                Surveys:
                              </span>{" "}
                              <span className="font-semibold text-slate-900">
                                {row.survey}
                              </span>
                            </div>
                            {row.surveyTimes.length > 0 && (
                              <div className="rounded bg-emerald-50 px-1.5 py-1 text-[11px] text-emerald-900">
                                Submitted: {row.surveyTimes.join(", ")} MST
                              </div>
                            )}
                            <div className="border-t pt-1 font-semibold">
                              Total records: {row.total.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="periodic"
                    name="Periodic Data"
                    stackId="sensors"
                    fill="#2563EB"
                    maxBarSize={36}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="other"
                    name="Other Sensor Data"
                    stackId="sensors"
                    fill="#94A3B8"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={36}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="survey"
                    name="Surveys (right axis)"
                    fill="#059669"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={28}
                  >
                    <LabelList
                      dataKey="survey"
                      position="top"
                      formatter={(val: unknown) =>
                        Number(val) > 0 ? String(val) : ""
                      }
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        fill: "#047857",
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {analysis.surveyCount > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-2.5 text-xs">
                  <span className="font-medium text-slate-700">
                    Survey submissions (MST):
                  </span>
                  {analysis.dailyCollection
                    .filter((d) => d.survey > 0)
                    .map((d) => (
                      <span
                        key={d.day}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50/80 px-2 py-0.5 text-[11px] text-emerald-900"
                      >
                        <span className="font-semibold">{d.label}:</span>
                        <span className="font-mono">
                          {d.surveyTimes.join(", ")}
                        </span>
                      </span>
                    ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-2 text-sm font-medium text-[#1E293B]">
                Periodic Sensor Sources Breakdown
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Source</th>
                      <th className="py-2 px-3 font-medium">
                        Events in Window
                      </th>
                      <th className="py-2 px-3 font-medium">
                        Latest Event in Range
                      </th>
                      <th className="py-2 pl-3 font-medium">
                        Max Single-Source Gap
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {analysis.perSource.map((s) => (
                      <tr key={s.source}>
                        <td className="py-2 pr-3 font-medium text-slate-900">
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="inline-block size-2.5 rounded-full"
                              style={{ backgroundColor: s.color }}
                            />
                            {s.label}
                          </span>
                        </td>
                        <td className="py-2 px-3">{s.count}</td>
                        <td className="py-2 px-3 font-mono text-[11px]">
                          {s.lastEventIso
                            ? formatMstWithSeconds(s.lastEventIso)
                            : "None"}
                        </td>
                        <td className="py-2 pl-3 font-medium">
                          <span
                            className={
                              s.maxGapSeconds >= 3600
                                ? "text-red-700"
                                : "text-slate-700"
                            }
                          >
                            {formatGapDuration(s.maxGapSeconds)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
